/**
 * SystemsHierarchy — domain › group › family › component, resolved from project
 * inputs. TS mirror of pipeline/bim/systems_hierarchy.py over the same JSON.
 *
 * Follows IfcProductExtension's organizing concepts: a GROUP is a functional system
 * (IfcBuiltSystem, IfcDistributionSystem, IfcZone) decided in a known phase; a FAMILY
 * is one catalog / authoring module / detail kind with its IFC class; a COMPONENT is a
 * catalog entry drawn as a detail. Our SYSTEMS (profiles) stay the unit the rules are
 * written against; a `material_profile` system (mass timber) is shown as a tag on the
 * groups its families belong to, never as a branch.
 *
 * The landing page, the domain pages, the Navigator and the Info dock all read this
 * one source. Applicability (required / recommended / optional / not_applicable) is
 * derived from Project Setup; the detail components per family come from the
 * published library index (/details/index.json) when it can be fetched.
 */

import hierarchy from '../../scripts/ifc_worker/pipeline/bim/data/systems_hierarchy.json';
import communityTypes from '../../scripts/ifc_worker/pipeline/bim/data/community_building_types.json';
import designBuildPattern from '../../scripts/ifc_worker/pipeline/bim/data/design_build_pattern.json';
import type { PhaseStatus } from './DomainRegistry.js';
import type { ProjectSetup } from './ProjectSetup.js';

export type Applicability = 'required' | 'recommended' | 'optional' | 'not_applicable';
export const LEVEL_ORDER: Applicability[] = ['required', 'recommended', 'optional', 'not_applicable'];
export const LEVEL_LABEL: Record<Applicability, string> = {
    required: 'required', recommended: 'recommended', optional: 'optional', not_applicable: 'not applicable',
};

type Cond = { any?: Cond[]; all?: Cond[]; input?: string; equals?: unknown; contains?: string; truthy?: boolean; gte?: number };
interface Rule { when?: Cond; level: Applicability; reason: string }
interface FamilyDef {
    id: string; group: string; title: string; catalog: string | null; authoring?: string | null; ifc_class: string; assembly?: string | null;
    decided_in: string[]; detail_kinds: string[]; specs: string[]; tier?: string; status: string; applies: Rule[];
}
interface SystemDef {
    id: string; domain: 'building' | 'landscape'; kind: 'function' | 'material_profile'; material_tag?: string; title: string; status: string;
    blurb: string; ifc: string; skills: string[]; applies: Rule[]; families: FamilyDef[];
}
interface GroupDef { id: string; domain: 'building' | 'landscape'; title: string; phase: string; order: number; ifc: { kind: string; class: string; predefined_type: string | null }; blurb: string }
interface PhaseDef { label: string; rank: number; blurb: string }

export interface Inputs {
    structural_system: string | null; community_types: string[]; community_systems: string[]; occupancies: string[];
    track: 'commercial' | 'residential'; site_work: boolean; structure_count: number; demolition_count: number;
    storeys: number; construction_type: string | null; has_office: boolean; has_assembly: boolean; has_dwelling: boolean; has_roof: boolean;
}

export interface DetailComponentRef { id: string; title?: string; kind?: string; entry?: string; file?: string; provisional?: boolean; reference_only?: boolean }

export interface ResolvedFamily {
    id: string; group: string; title: string; catalog: string | null; authoring?: string | null; ifc_class: string; assembly?: string | null;
    decided_in: string[]; phase_label: string; phase_rank: number;
    detail_kinds: string[]; specs: string[]; tier?: string; status: PhaseStatus;
    system: string; system_title: string; material_tag: string | null;
    level: Applicability; reason: string; components: DetailComponentRef[];
}
export interface ResolvedSystem {
    id: string; domain: 'building' | 'landscape'; kind: 'function' | 'material_profile'; material_tag?: string; title: string; status: PhaseStatus;
    blurb: string; ifc: string; skills: string[]; level: Applicability; reason: string; families: ResolvedFamily[];
}
export interface ResolvedGroup {
    id: string; domain: 'building' | 'landscape'; title: string; blurb: string; phase: string; phase_label: string; phase_rank: number;
    ifc: { kind: string; class: string; predefined_type: string | null }; ifc_kind_blurb: string; material_tags: string[];
    level: Applicability; reason: string; families: ResolvedFamily[]; detail_count: number;
}
export interface Resolved { inputs: Inputs | null; groups: ResolvedGroup[]; systems: ResolvedSystem[] }

const DATA = hierarchy as unknown as { systems: SystemDef[]; groups: GroupDef[]; phases: Record<string, { blurb: string }>; ifc_kinds: Record<string, string> };
const SYSTEMS = DATA.systems;
const GROUPS = DATA.groups;
/**
 * One phase spine: the hierarchy keys its phases by the design-build pattern's ids and carries
 * only a blurb; label (the pattern's short name) and rank (the pattern's order) come from the
 * pattern, as in pipeline/bim/systems_hierarchy.py.
 */
const PATTERN_PHASES = (designBuildPattern.phases as { id: string; title: string; short?: string }[]);
export const PHASES: Record<string, PhaseDef> = Object.fromEntries(Object.entries(DATA.phases).map(([id, ph]) => {
    const i = PATTERN_PHASES.findIndex(p => p.id === id);
    const p = PATTERN_PHASES[i];
    return [id, { label: p?.short ?? p?.title ?? id, rank: i < 0 ? 9 : i, blurb: ph.blurb }];
}));
export const IFC_KINDS = DATA.ifc_kinds;
const TYPES = [...(communityTypes.building_types as any[]), ...(communityTypes.site_types as any[])] as { id: string; system?: string; occupancies?: string[] }[];

export function systemDefs(domain?: 'building' | 'landscape'): SystemDef[] {
    return domain ? SYSTEMS.filter(s => s.domain === domain) : SYSTEMS.slice();
}
export function groupDefs(domain?: 'building' | 'landscape'): GroupDef[] {
    const gs = domain ? GROUPS.filter(g => g.domain === domain) : GROUPS.slice();
    return gs.sort((a, b) => (a.domain === b.domain ? 0 : a.domain === 'building' ? -1 : 1) || a.order - b.order);
}
export function phaseLabel(decided: string[]): string {
    const labels = decided.map(p => PHASES[p]?.label ?? p);
    return labels.length > 1 ? labels.join(' → ') : (labels[0] ?? '');
}

export function deriveInputs(setup: ProjectSetup, opts: { storeys?: number; construction_type?: string | null } = {}): Inputs {
    const types = setup.structures.map(s => s.community_type).filter((t): t is string => !!t);
    const systems = new Set<string>(); const occ = new Set<string>();
    for (const tid of types) {
        const t = TYPES.find(x => x.id === tid);
        if (!t) continue;
        if (t.system) systems.add(t.system);
        for (const o of t.occupancies ?? []) occ.add(o);
    }
    const residentialOnly = types.length > 0 && types.every(t => t === 'farm_dwelling' || t === 'employee_housing');
    const track: Inputs['track'] = setup.track === 'commercial' || setup.track === 'residential' ? setup.track : (residentialOnly ? 'residential' : 'commercial');
    return {
        structural_system: setup.inherited.structural_system ?? null,
        community_types: [...new Set(types)].sort(), community_systems: [...systems].sort(), occupancies: [...occ].sort(),
        track, site_work: !!setup.site_work, structure_count: setup.structures.length, demolition_count: setup.demolitions.length,
        storeys: Math.max(opts.storeys ?? 0, ...setup.structures.map(s => s.storeys ?? 1), 1), construction_type: opts.construction_type ?? null,
        has_office: occ.has('B'), has_assembly: occ.has('A-3') || occ.has('E'),
        has_dwelling: track === 'residential' || types.some(t => t === 'farm_dwelling' || t === 'employee_housing'),
        has_roof: setup.structures.some(s => (s.kind ?? 'building') === 'building'),
    };
}

export function evaluate(cond: Cond | undefined, inputs: Inputs): boolean {
    if (!cond) return true;
    if (cond.any) return cond.any.some(c => evaluate(c, inputs));
    if (cond.all) return cond.all.every(c => evaluate(c, inputs));
    const v = (inputs as unknown as Record<string, unknown>)[cond.input ?? ''];
    if ('equals' in cond) return v === cond.equals;
    if ('contains' in cond) return Array.isArray(v) && v.includes(cond.contains);
    if ('truthy' in cond) return !!v === !!cond.truthy;
    if ('gte' in cond) return typeof v === 'number' && v >= (cond.gte as number);
    return false;
}

function apply(rules: Rule[], inputs: Inputs | null): { level: Applicability; reason: string } {
    if (!inputs) return { level: 'optional', reason: 'no project setup yet' };
    for (const r of rules) if (evaluate(r.when, inputs)) return { level: r.level, reason: r.reason };
    return { level: 'optional', reason: '' };
}

let detailIndex: (DetailComponentRef & { catalog?: string })[] | null = null;
/** Fetch the published detail library index once (optional enrichment; failures leave components empty). */
export async function loadDetailIndex(): Promise<DetailComponentRef[]> {
    if (detailIndex) return detailIndex;
    try {
        const r = await fetch('/details/index.json');
        const j = await r.json();
        detailIndex = (j.components as any[]).map(c => ({ id: c.id, title: c.title, kind: c.kind, entry: c.entry, file: c.file, provisional: c.provisional, reference_only: c.referenceOnly, catalog: c.catalog }));
    } catch { detailIndex = []; }
    return detailIndex;
}

function componentsFor(f: FamilyDef, comps: (DetailComponentRef & { catalog?: string })[]): DetailComponentRef[] {
    if (!f.catalog) return [];
    const kinds = new Set(f.detail_kinds ?? []);
    return comps.filter(c => c.catalog === f.catalog && (!kinds.size || (c.kind && kinds.has(c.kind)))).map(({ catalog: _c, ...rest }) => rest);
}

/** True when the setup carries enough to resolve (a structure, site work, or a bound facility). */
export function hasProjectInputs(setup: ProjectSetup | null): boolean {
    return !!setup && (setup.structures.length > 0 || setup.site_work || !!setup.inherited.facility_id);
}

export function resolveAll(setup: ProjectSetup | null, opts: { storeys?: number; construction_type?: string | null } = {}, comps: (DetailComponentRef & { catalog?: string })[] = detailIndex ?? []): Resolved {
    const inputs = hasProjectInputs(setup) ? deriveInputs(setup!, opts) : null;
    const systems: ResolvedSystem[] = SYSTEMS.map(s => {
        const sys = apply(s.applies, inputs);
        const families: ResolvedFamily[] = (s.families ?? []).map(f => {
            let fam = apply(f.applies, inputs);
            if (sys.level === 'not_applicable' && fam.level !== 'not_applicable') fam = { level: 'not_applicable', reason: `system not applicable: ${sys.reason}` };
            const decided = f.decided_in ?? [];
            return {
                id: f.id, group: f.group, title: f.title, catalog: f.catalog ?? null, authoring: f.authoring ?? null, ifc_class: f.ifc_class, assembly: f.assembly ?? null,
                decided_in: decided, phase_label: phaseLabel(decided), phase_rank: Math.min(9, ...decided.map(p => PHASES[p]?.rank ?? 9)),
                detail_kinds: f.detail_kinds ?? [], specs: f.specs ?? [], tier: f.tier, status: f.status as PhaseStatus,
                system: s.id, system_title: s.title, material_tag: s.kind === 'material_profile' ? (s.material_tag ?? s.title) : null,
                level: fam.level, reason: fam.reason, components: componentsFor(f, comps),
            };
        });
        return { id: s.id, domain: s.domain, kind: s.kind, material_tag: s.material_tag, title: s.title, status: s.status as PhaseStatus, blurb: s.blurb, ifc: s.ifc, skills: s.skills ?? [], level: sys.level, reason: sys.reason, families };
    });
    systems.sort((a, b) => (a.domain === b.domain ? 0 : a.domain === 'building' ? -1 : 1) || LEVEL_ORDER.indexOf(a.level) - LEVEL_ORDER.indexOf(b.level));

    const groups: ResolvedGroup[] = groupDefs().map(g => {
        const families = systems.flatMap(s => s.families).filter(f => f.group === g.id)
            .sort((a, b) => a.phase_rank - b.phase_rank || LEVEL_ORDER.indexOf(a.level) - LEVEL_ORDER.indexOf(b.level));
        let level: Applicability = 'optional'; let reason = inputs ? 'no families yet' : 'no project setup yet';
        if (inputs && families.length) {
            const best = families.reduce((a, b) => (LEVEL_ORDER.indexOf(b.level) < LEVEL_ORDER.indexOf(a.level) ? b : a));
            level = best.level; reason = best.reason;
        }
        const ph = PHASES[g.phase];
        return {
            id: g.id, domain: g.domain, title: g.title, blurb: g.blurb, phase: g.phase, phase_label: ph?.label ?? g.phase, phase_rank: ph?.rank ?? 9,
            ifc: g.ifc, ifc_kind_blurb: IFC_KINDS[g.ifc.kind] ?? '',
            material_tags: [...new Set(families.filter(f => f.material_tag && f.level !== 'not_applicable').map(f => f.material_tag as string))].sort(),
            level, reason, families, detail_count: families.reduce((n, f) => n + f.components.length, 0),
        };
    });
    return { inputs, groups, systems };
}

/** Back-compat: the profiles only. */
export function resolveHierarchy(setup: ProjectSetup | null, opts: { storeys?: number; construction_type?: string | null } = {}): ResolvedSystem[] {
    return resolveAll(setup, opts).systems;
}
